import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';

/**
 * Web2 mirror of `apps/mobile/lib/parent/childSummary.ts`. Reads the
 * latest analytics_snapshot, active goal cards, and newest development
 * plan parent_summary for one child. RLS does the gating — see §10.1
 * "never recompute analytics on read".
 *
 * Surfaced on the parent's `/parent/child/:id` detail view (Wave 3.4).
 */

export interface ChildGoal {
  id: string;
  cornerId: string | null;
  description: string;
  targetScore: number | null;
  targetDate: string | null;
  status: string;
}

export interface ChildSummary {
  cornerScores: Record<string, number>;
  goalCards: ChildGoal[];
  parentSummary: string | null;
  /** ISO date of the most recent snapshot — null if none yet. */
  snapshotDate: string | null;
}

interface State {
  data: ChildSummary | null;
  loading: boolean;
  error: string | null;
}

const EMPTY: ChildSummary = {
  cornerScores: {},
  goalCards: [],
  parentSummary: null,
  snapshotDate: null,
};

export function useChildSummary(playerId: string | null): State {
  const [state, setState] = useState<State>({ data: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    if (!playerId) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState({ data: null, loading: true, error: null });

    (async () => {
      const [snapRes, goalsRes, planRes] = await Promise.all([
        supabase
          .from('analytics_snapshots')
          .select('corner_scores, snapshot_date')
          .eq('player_id', playerId)
          .order('snapshot_date', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('goal_cards')
          .select('id, corner_id, description, target_score, target_date, status')
          .eq('player_id', playerId)
          .eq('status', 'ACTIVE')
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('development_plans')
          .select('parent_summary, updated_at')
          .eq('player_id', playerId)
          .is('deleted_at', null)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (cancelled) return;

      // Soft-fail per query — RLS or missing tables shouldn't make the
      // page unusable. Empty values render as the empty-state cards.
      const summary: ChildSummary = {
        cornerScores: (snapRes.data?.corner_scores as Record<string, number> | undefined) ?? {},
        snapshotDate: (snapRes.data?.snapshot_date as string | null | undefined) ?? null,
        goalCards: (goalsRes.data ?? []).map((g: any) => ({
          id: String(g.id),
          cornerId: (g.corner_id as string | null) ?? null,
          description: String(g.description ?? ''),
          targetScore: (g.target_score as number | null) ?? null,
          targetDate: (g.target_date as string | null) ?? null,
          status: String(g.status ?? 'ACTIVE'),
        })),
        parentSummary: (planRes.data?.parent_summary as string | null | undefined) ?? null,
      };

      setState({ data: summary.cornerScores ? summary : EMPTY, loading: false, error: null });
    })();

    return () => { cancelled = true; };
  }, [playerId]);

  return state;
}
